import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Clock, LogIn, LogOut, Plus, Calendar, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

const TimeEntries = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  
  // Debug: sprawdź czy użytkownik jest Super Adminem
  useEffect(() => {
    if (user) {
      console.log('TimeEntries - User role:', user.role);
      console.log('TimeEntries - Is Super Admin:', isSuperAdmin);
    }
  }, [user, isSuperAdmin]);
  
  const [weekStart, setWeekStart] = useState(() => {
    // Ustaw na poniedziałek bieżącego tygodnia
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
    const monday = new Date(today.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday.toISOString().split('T')[0];
  });
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  useEffect(() => {
    fetchEmployee();
    fetchTimeEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, weekStart]);

  const fetchEmployee = async () => {
    try {
      const response = await api.get(`/employees/${employeeId}`);
      setEmployee(response.data);
    } catch (error) {
      toast.error('Błąd podczas pobierania danych pracownika');
      console.error('Error fetching employee:', error);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      setLoading(true);
      // Pobierz wpisy dla całego tygodnia (od poniedziałku do niedzieli)
      const startDate = new Date(weekStart);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6); // Dodaj 6 dni do poniedziałku, żeby uzyskać niedzielę
      
      // Pobierz wszystkie wpisy i przefiltruj po tygodniu
      const response = await api.get(`/employees/${employeeId}/time-entries`);
      const allEntries = response.data;
      
      // Filtruj wpisy dla wybranego tygodnia
      const weekEntries = allEntries.filter(entry => {
        const entryDate = new Date(entry.entryTime);
        return entryDate >= startDate && entryDate <= endDate;
      });
      
      setTimeEntries(weekEntries);
    } catch (error) {
      toast.error('Błąd podczas pobierania wpisów czasu');
      console.error('Error fetching time entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async (data) => {
    setSaving(true);
    try {
      let entryTime;
      if (data.entryTime && data.entryTimeTime) {
        entryTime = `${data.entryTime}T${data.entryTimeTime}:00`;
      } else {
        entryTime = new Date().toISOString();
      }
      
      const entryData = {
        entryTime: entryTime,
        entryType: data.entryType,
        notes: data.notes || null
      };
      await api.post(`/employees/${employeeId}/time-entries`, entryData);
      toast.success('Wpis czasu został dodany');
      setShowAddModal(false);
      reset();
      fetchTimeEntries();
    } catch (error) {
      toast.error('Błąd podczas dodawania wpisu czasu');
      console.error('Error adding time entry:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten wpis czasu?')) {
      return;
    }

    setDeleting(entryId);
    try {
      await api.delete(`/employees/time-entries/${entryId}`);
      toast.success('Wpis czasu został usunięty');
      fetchTimeEntries();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Błąd podczas usuwania wpisu czasu';
      toast.error(errorMessage);
      console.error('Error deleting time entry:', error);
    } finally {
      setDeleting(null);
    }
  };


  const getWeekDays = () => {
    const start = new Date(weekStart);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getEntriesForDay = (day) => {
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day);
    dayEnd.setHours(23, 59, 59, 999);
    
    return timeEntries
      .filter(entry => {
        const entryDate = new Date(entry.entryTime);
        return entryDate >= dayStart && entryDate <= dayEnd;
      })
      .sort((a, b) => new Date(a.entryTime) - new Date(b.entryTime));
  };

  const calculateWorkTimeForDay = (dayEntries) => {
    if (dayEntries.length === 0) return null;
    
    let totalMinutes = 0;
    let lastEntry = null;
    
    for (const entry of dayEntries) {
      if (entry.entryType === 'ENTRY') {
        lastEntry = new Date(entry.entryTime);
      } else if (entry.entryType === 'EXIT' && lastEntry) {
        const exitTime = new Date(entry.entryTime);
        const diffMinutes = (exitTime - lastEntry) / (1000 * 60);
        totalMinutes += diffMinutes;
        lastEntry = null;
      }
    }
    
    if (totalMinutes === 0) return null;
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    return `${hours}h ${minutes}m`;
  };

  const calculateTotalWorkTime = () => {
    const weekDays = getWeekDays();
    let totalMinutes = 0;
    
    weekDays.forEach(day => {
      const dayEntries = getEntriesForDay(day);
      const dayTime = calculateWorkTimeForDay(dayEntries);
      if (dayTime) {
        const match = dayTime.match(/(\d+)h\s*(\d+)m/);
        if (match) {
          totalMinutes += parseInt(match[1]) * 60 + parseInt(match[2]);
        }
      }
    });
    
    if (totalMinutes === 0) return null;
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    return `${hours}h ${minutes}m`;
  };

  const changeWeek = (direction) => {
    const currentWeekStart = new Date(weekStart);
    currentWeekStart.setDate(currentWeekStart.getDate() + (direction === 'next' ? 7 : -7));
    setWeekStart(currentWeekStart.toISOString().split('T')[0]);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDayName = (date) => {
    return date.toLocaleDateString('pl-PL', { weekday: 'long' });
  };

  const formatTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !employee) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const weekDays = getWeekDays();
  const totalWorkTime = calculateTotalWorkTime();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/app/hr/employees/list')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {employee ? `${employee.firstName} ${employee.lastName}` : 'Godziny pracy'}
            </h1>
            {employee && (
              <p className="text-sm text-gray-500 mt-1">{employee.email}</p>
            )}
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Dodaj wpis
        </button>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => changeWeek('prev')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              title="Poprzedni tydzień"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                {formatDate(weekDays[0])} - {formatDate(weekDays[6])}
              </span>
            </div>
            <button
              onClick={() => changeWeek('next')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              title="Następny tydzień"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const day = today.getDay();
                const diff = today.getDate() - day + (day === 0 ? -6 : 1);
                const monday = new Date(today.setDate(diff));
                monday.setHours(0, 0, 0, 0);
                setWeekStart(monday.toISOString().split('T')[0]);
              }}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Dzisiaj
            </button>
          </div>
          {totalWorkTime && (
            <div className="flex items-center gap-2 text-lg font-semibold text-primary-600">
              <Clock className="w-5 h-5" />
              <span>Łączny czas tygodnia: {totalWorkTime}</span>
            </div>
          )}
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Dzień
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Wpisy czasu
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Czas pracy
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {weekDays.map((day, index) => {
                    const dayEntries = getEntriesForDay(day);
                    const dayWorkTime = calculateWorkTimeForDay(dayEntries);
                    const isToday = day.toDateString() === new Date().toDateString();
                    
                    return (
                      <tr key={index} className={isToday ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatDayName(day)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDate(day)}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {dayEntries.length === 0 ? (
                            <span className="text-sm text-gray-400">Brak wpisów</span>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {dayEntries.map((entry) => (
                                <div
                                  key={entry.id}
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                    entry.entryType === 'ENTRY'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {entry.entryType === 'ENTRY' ? (
                                    <LogIn className="w-3 h-3" />
                                  ) : (
                                    <LogOut className="w-3 h-3" />
                                  )}
                                  <span>{formatTime(entry.entryTime)}</span>
                                  {entry.notes && (
                                    <span className="ml-1 text-gray-600" title={entry.notes}>
                                      ({entry.notes.substring(0, 20)}...)
                                    </span>
                                  )}
                                  {isSuperAdmin && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteEntry(entry.id);
                                      }}
                                      disabled={deleting === entry.id}
                                      className="ml-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                      title="Usuń wpis"
                                    >
                                      {deleting === entry.id ? (
                                        <LoadingSpinner size="sm" />
                                      ) : (
                                        <Trash2 className="w-4 h-4" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {dayWorkTime ? (
                            <div className="flex items-center gap-1 text-sm font-semibold text-primary-600">
                              <Clock className="w-4 h-4" />
                              <span>{dayWorkTime}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal dodawania wpisu */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Dodaj wpis czasu</h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    reset();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSubmit(handleAddEntry)} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Typ *</label>
                  <select
                    {...register('entryType', { required: 'Typ jest wymagany' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Wybierz typ</option>
                    <option value="ENTRY">Wejście</option>
                    <option value="EXIT">Wyjście</option>
                  </select>
                  {errors.entryType && (
                    <p className="mt-1 text-sm text-red-600">{errors.entryType.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Data</label>
                  <input
                    {...register('entryTime')}
                    type="date"
                    defaultValue={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Godzina</label>
                  <input
                    {...register('entryTimeTime')}
                    type="time"
                    defaultValue={new Date().toTimeString().slice(0, 5)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Uwagi</label>
                  <textarea
                    {...register('notes')}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Opcjonalne uwagi..."
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      reset();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Anuluj
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {saving ? 'Zapisywanie...' : 'Zapisz'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeEntries;
